import { Observable, Subject } from 'rxjs';
import { debounceTime, map, takeUntil } from 'rxjs/operators';

import { SelectionModel } from '@angular/cdk/collections';
import { CdkDragRelease, CdkDragStart } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { ISnFeedQueryParameters, ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { SnCamera, SnCameraGroup, SnCameraStatus } from '@lib/api/entities';
import { SnCamerasAPI } from '@lib/api/services';
import { SnDataListComponent } from '@lib/ui/data-list';

@Component({
  selector: 'sn-camera-search',
  templateUrl: './camera-search.component.html',
  styleUrls: ['./camera-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CameraSearchComponent implements OnInit, OnDestroy {
  @ViewChild('list', { static: false })
  public readonly list: SnDataListComponent<SnCamera>;
  public readonly loadData = this._loadData.bind(this);
  public readonly feedData = this._feedData.bind(this);

  @Input() public multiple: boolean;
  @Input() public params: ISnListQueryParameters = {};
  @Input() public radius: number;
  @Input() public dragTool: boolean;
  @Input() public distanceCol: boolean;
  @Input() public activeCameras: any[] = [];
  @Input() public showPreviewCam = false;
  @Output() public readonly selectedCam = new EventEmitter<SnCamera>();
  @Output() public readonly add = new EventEmitter<SnCamera>();
  @Output() public readonly selectedCams = new EventEmitter<SnCamera[]>();
  @Output() public readonly goTo = new EventEmitter<SnCamera>();
  @Output() public readonly remove = new EventEmitter<SnCamera>();

  public previewCamId: string;
  public selection = new SelectionModel<SnCamera>(true, []);
  public cameraGroups: SnCameraGroup[];

  public readonly searchControl: UntypedFormControl = new UntypedFormControl();
  public columns: string[] = ['name', 'location', 'floor', 'owner'];
  public _querySearch: string;

  public pageSize;

  public selected;
  public currentCamera: SnCamera;

  private readonly _destroy$ = new Subject();

  constructor(private readonly _camerasAPI: SnCamerasAPI) {}

  public ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(300), takeUntil(this._destroy$))
      .subscribe((v) => {
        this._querySearch = v;
        this._reloadData();
      });

    this.selected = '20';
    if (this.distanceCol) {
      this.columns.unshift('distance');
    }
    if (this.dragTool) {
      this.columns.unshift('dragTool');
    }
  }

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public submitForm(): void {
    this._querySearch = this.searchControl.value;
    this._reloadData();
  }

  public clear(): void {
    this.searchControl.reset(null);
    this.submitForm();
  }

  public onSelected(camera: any): void {
    if (this.isActive(camera.id)) {
      this.goTo.next(camera);
      return;
    }
    if (this.multiple) {
      this.selection.toggle(camera);
    } else {
      this.selectedCam.next(camera);
      if (this.showPreviewCam) {
        this.previewCamId = camera.id;
      }
    }
  }

  public loadAllLocations(): void {
    this.pageSize = 'all';
    this._reloadData();
  }

  public loadTenLocations(): void {
    this.pageSize = 10;
    this._reloadData();
  }

  public loadTwentyLocations(): void {
    this.pageSize = 20;
    this._reloadData();
  }

  public isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.list.dataSource.data.length;
    return numSelected === numRows;
  }

  public isActive(id: string): boolean {
    if (!this.activeCameras.length) {
      return;
    }
    return this.activeCameras.findIndex((active) => active.id === id) > -1;
  }

  public masterToggle(): void {
    this.isAllSelected()
      ? this.selection.clear()
      : this.list.dataSource.data.forEach((row) => this.selection.select(row));
  }

  public addCameras(): void {
    const cameras = this.selection.selected.map((camera) => camera);
    this.selectedCams.next(cameras);
  }

  public onSearchValueChange(filterValue: string): void {
    this._querySearch = filterValue;
    this._reloadData();
  }

  public onDragRelease(release: CdkDragRelease, camera: SnCamera): void {
    const source = release.source;
    this.add.next(camera);
    source.reset();
    source.element.nativeElement.style.position = 'initial';
  }

  public onDragStart(event: CdkDragStart, camera: SnCamera): void {
    const element = event.source.element.nativeElement;
    const box = element.getBoundingClientRect();
    element.style.top = `${box.top}px`;
    element.style.left = `${box.left}px`;
    element.style.position = 'fixed';
  }

  public determineRotate(camera: SnCamera): boolean {
    const targetIndex = this.activeCameras.findIndex((active) => active.id === camera.id);
    if (targetIndex > -1) {
      camera.infospotHeading = this.activeCameras[targetIndex].infospotHeading;
      return camera.infospotHeading === 90;
    }
    return camera.infospotHeading === 90;
  }

  public getIcon(camera: SnCamera): any {
    const icon: any = {
      fontSet: 'fs',
    };
    if (this.isActive(camera.id)) {
      const targetIndex = this.activeCameras.findIndex((active) => active.id === camera.id);
      if (targetIndex > -1) {
        icon.fontIcon = this.activeCameras[targetIndex].cameraImage;
      }
      return icon;
    }
    let status = 'Exists';
    const ptz = camera.type === 'PTZ';
    if (camera.isParentCamera) {
      icon.fontIcon = 'quad_camera_lite';
      icon.color =
        camera.status === SnCameraStatus.Up
          ? '#67BD4D'
          : camera.status === SnCameraStatus.Down
          ? '#FF3535'
          : camera.status === SnCameraStatus.Unmanaged
          ? '#FFB028'
          : 'rgb(137, 137, 137);';
      return icon;
    }
    switch (camera.status) {
      case SnCameraStatus.Up:
        status = 'Up';
        break;
      case SnCameraStatus.Down:
        status = 'Down';
        break;
      case SnCameraStatus.Unmanaged:
        icon.fontIcon = 'CameraUnmanaged';
        return icon;
    }
    if (ptz) {
      icon.fontIcon = camera.isAiEnabled ? `Camera${status}_AI_PTZ` : `Camera${status}_PTZ`;
    } else {
      icon.fontIcon = camera.isAiEnabled ? `Camera${status}_AI` : `Icon_Camera${status}`;
    }
    switch (camera.infospotHeading) {
      case 180:
        icon.fontIcon = `${icon.fontIcon}_down`;
        break;
      case 270:
        icon.fontIcon = `${icon.fontIcon}_left`;
        break;
      case 360:
        icon.fontIcon = `${icon.fontIcon}_up`;
        break;
      default:
        return icon;
    }
    return icon;
  }

  public cameraOrientation(camera: SnCamera, heading: number): void {
    camera.infospotHeading = heading;
  }

  public convertToMeters(miles: number): number {
    return Math.round(miles * 1609);
  }

  private _reloadData(): void {
    this.list.reference.reload();
  }

  private _loadData(params?: ISnListQueryParameters): Observable<ISnListResponse<SnCamera>> {
    params.search = this._querySearch;
    return this._camerasAPI.getCameraList(this._prepareQueryParams(params)).pipe(
      map((response: any) => {
        return this.radius
          ? {
              count: response.count,
              totalPages: response.totalPages,
              results: response.results.filter((r) => {
                return r.distance <= this.radius;
              }),
            }
          : response;
      }),
    );
  }

  private _feedData(params: ISnFeedQueryParameters): Observable<SnCamera[]> {
    return this._camerasAPI.feedCameras(this._prepareQueryParams(params));
  }

  private _prepareQueryParams<T extends ISnListQueryParameters>(params: any): any {
    return {
      ordering: {
        active: 'name',
        direction: 'asc',
      },
      search: this._querySearch,
      page: params.page,
      pageSize: this.pageSize ? this.pageSize : params.pageSize,
      ...this.params,
    };
  }
}
